const HEX_COLOR_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

// Converts #rgb / #rrggbb / #rrggbbaa to rgba(); any other CSS colour (rgba(), var(), named) is returned unchanged
export const hexToRgba = (hex: string | undefined | null, alpha: number): string => {
    if (!hex || !hex.trim()) {
        return `rgba(0, 0, 0, ${alpha})`;
    }

    const value = hex.trim();
    const match = value.match(HEX_COLOR_PATTERN);
    if (!match) {
        return value;
    }

    const digits = match[1].length === 3
        ? match[1].split('').map((char) => char + char).join('')
        : match[1];

    const r = parseInt(digits.substring(0, 2), 16);
    const g = parseInt(digits.substring(2, 4), 16);
    const b = parseInt(digits.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const clamp = (value: number) => Math.min(255, Math.max(0, Math.round(value)));

const normalizeHex = (hex: string) => {
    const sanitized = hex.replace('#', '').trim();
    if (sanitized.length === 3) {
        return sanitized
            .split('')
            .map((char) => char + char)
            .join('');
    }
    return sanitized.padEnd(6, '0').slice(0, 6);
};

export const hexToRgb = (hex: string) => {
    const normalized = normalizeHex(hex);
    const r = parseInt(normalized.substring(0, 2), 16);
    const g = parseInt(normalized.substring(2, 4), 16);
    const b = parseInt(normalized.substring(4, 6), 16);
    return { r, g, b };
};

const componentToHex = (component: number) =>
    clamp(component).toString(16).padStart(2, '0');

export const rgbToHex = (r: number, g: number, b: number) =>
    `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;

export const isColorDark = (hex: string) => {
    const { r, g, b } = hexToRgb(hex);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.55;
};

// Lightens (towards white) or darkens (towards black) a hex colour by the given ratio
export const adjustColor = (hex: string, ratio: number, lighten: boolean) => {
    const { r, g, b } = hexToRgb(hex);
    const adjustChannel = (channel: number) =>
        lighten
            ? channel + (255 - channel) * ratio
            : channel * (1 - ratio);
    return rgbToHex(
        adjustChannel(r),
        adjustChannel(g),
        adjustChannel(b)
    );
};
